import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getVoices, getTtsConfig, ttsSend } from './async'
import { updateVoice, uploadVoiceAvatar, uploadVoiceSample } from '../voiceGroups/async'
import { logout } from '../user/userSlice'
import { IConfigVoice, IStateTts, ITtsMessages, IVoice, IVoiceItem, TSex } from '../../../@types/tts'
import { initialTtsSettings } from './config'
import { getLocalStorageInfo, setLocalStorageInfo } from '../../../tools'

export const initialFilterLanguages = ['ru', 'en', 'kz']
export const initialSexFilter: TSex[] = ['male', 'female']
const initialVoicesWithPicked = getLocalStorageInfo('tts')?.voices

const initialState: IStateTts = {
  voices: initialVoicesWithPicked || [],
  voiceLanguages: [],
  ttsConfig: [],
  messages: getLocalStorageInfo('tts')?.messages || [],
  settings: getLocalStorageInfo('tts')?.settings || initialTtsSettings,
  showEmotions: false,
  filters: {
    language: initialFilterLanguages,
    sex: initialSexFilter,
  },
  listFilters: {
    language: initialFilterLanguages,
    sex: '',
    sanitize: 'none',
    searchString: '',
  },
  loading: false,
  currentAudio: {
    id: '',
    currentTime: 0,
    totalTime: 0,
  },
}

const mapVoiceItems = (items: IVoiceItem[], existingVoices: IVoice[], preserveEmotions = false): IVoice[] => {
  const newVoices: IVoice[] = items.map((v) => {
    const emotions: string[] = Array.isArray(v.emotions) ? v.emotions.map((e: any) => e.technical_name).filter(Boolean) : []
    const defaultEmotion = Array.isArray(v.emotions) ? v.emotions.find((e: any) => e.is_default)?.technical_name : undefined
    const sex: TSex = v.gender === 'male' || v.gender === 'female' ? v.gender : ''
    return {
      name: v.technical_name,
      title: v.display_name || v.technical_name,
      sex,
      picked: false,
      languages: v.language_code ? [v.language_code.toLowerCase()] : [],
      avatar: v.avatar_url || '',
      previewAudio: v.sample_url || '',
      availableEmotions: emotions,
      emotion: defaultEmotion ?? emotions[0],
      defaultEmotion: defaultEmotion ?? emotions[0],
      tooltip: v.disclaimer ?? undefined,
    }
  })

  return newVoices.map((newVoice) => {
    const existingVoice = existingVoices.find((v) => v.name === newVoice.name)
    if (!existingVoice) return newVoice
    const existingEmotions = Array.isArray(existingVoice.availableEmotions) ? existingVoice.availableEmotions : []
    const isEmotionChanged = !preserveEmotions && JSON.stringify(existingEmotions) !== JSON.stringify(newVoice.availableEmotions)
    return {
      ...existingVoice,
      title: newVoice.title,
      sex: newVoice.sex,
      languages: newVoice.languages,
      avatar: newVoice.avatar,
      previewAudio: newVoice.previewAudio,
      availableEmotions: isEmotionChanged ? newVoice.availableEmotions : existingEmotions,
      emotion: isEmotionChanged ? newVoice.emotion : existingVoice.emotion,
      defaultEmotion: newVoice.defaultEmotion,
      tooltip: newVoice.tooltip,
    }
  })
}

const applyVoicesUpdate = (state: IStateTts, items: IVoiceItem[], preserveEmotions = false): IStateTts => {
  const updatedVoices = mapVoiceItems(items, state.voices || [], preserveEmotions)
  setLocalStorageInfo('tts', { ...getLocalStorageInfo('tts'), voices: updatedVoices })
  return { ...state, voices: updatedVoices }
}

const ttsSlice = createSlice({
  name: 'tts',
  initialState,
  reducers: {
    ttsUpdateSettings: (state, actions) => {
      const newSettings = { ...state.settings, ...actions.payload }
      setLocalStorageInfo('tts', { ...getLocalStorageInfo('tts'), settings: newSettings })
      return { ...state, settings: newSettings }
    },
    ttsUpdateModalFilters: (state, action) => {
      return { ...state, filters: { ...state.filters, ...action.payload } }
    },
    ttsUpdateListFilters: (state, action) => {
      return { ...state, listFilters: { ...state.listFilters, ...action.payload } }
    },
    ttsShowHideMessage: (state, action: PayloadAction<undefined | { id?: string; value?: boolean }>) => {
      const id = action?.payload?.id
      if (id) {
        const messages = state.messages.map((item) => (item.id === id ? { ...item, isOpened: !item.isOpened } : item))
        setLocalStorageInfo('tts', { ...getLocalStorageInfo('tts'), messages })
        return { ...state, messages }
      }

      const messages = state.messages.map((item) => ({ ...item, isOpened: action.payload?.value ?? false }))
      setLocalStorageInfo('tts', { ...getLocalStorageInfo('tts'), messages })

      return {
        ...state,
        messages,
      }
    },
    ttsSetAudio: (state, action) => {
      if (action.payload?.id && action.payload?.id !== state.currentAudio.id) state.currentAudio.id = action.payload.id
      if (action.payload.currentTime) state.currentAudio.currentTime = action.payload.currentTime
    },
    ttsSetRemovePickedVoice: (state, action) => {
      const voices = state.voices.map((item) =>
        action.payload.name === item.name ? { ...item, picked: !item.picked } : item
      )
      setLocalStorageInfo('tts', { ...getLocalStorageInfo('tts'), voices })
      return {
        ...state,
        voices,
      }
    },
    ttsSetEmotionVoice: (state, action) => {
      const voices = state.voices.map((item) =>
        action.payload.name === item.name ? { ...item, emotion: action.payload.emotion } : item
      )
      setLocalStorageInfo('tts', { ...getLocalStorageInfo('tts'), voices })
      return {
        ...state,
        voices,
      }
    },
    ttsUpdatePickedVoices: (state, action) => {
      const newVoices = action.payload
      setLocalStorageInfo('tts', { ...state, voices: newVoices })

      return {
        ...state,
        voices: newVoices,
      }
    },
    ttsClearMessages: (state) => {
      state.messages = []
      setLocalStorageInfo('tts', { ...getLocalStorageInfo('tts'), messages: [] })
    },
    ttsRemoveMessage: (state, action) => {
      const messages = state.messages.filter((item) => item.id !== action.payload.id)

      try {
        setLocalStorageInfo('tts', { ...getLocalStorageInfo('tts'), messages })
        return { ...state, messages }
      } catch (e) {
        return { ...state, messages }
      }
    },
    ttsSetUpdateCache: (state, action) => {
      return {
        ...state,
        settings: {
          ...state.settings,
          update_cache: action.payload,
        },
      }
    },
    ttsToggleShowEmotions: (state) => {
      return {
        ...state,
        showEmotions: !state.showEmotions,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ttsSend.pending, (state) => {
        state.loading = true
      })
      .addCase(ttsSend.fulfilled, (state, action: PayloadAction<ITtsMessages>) => {
        if (!action.payload?.response_audio_url || !Object.keys(action.payload).length) {
          state.loading = false
          return
        }

        const sanitizeText = action.payload.text.original.replace(/<[^>]*>/gi, '')
        const currentVoice = getLocalStorageInfo('tts')?.voices.find(
          (item: IVoice) => item.name === action.payload.voice
        )

        const newMessage = {
          ...action.payload,
          response_audio: '',
          languages: currentVoice?.languages || [],
          sex: currentVoice?.sex || '',
          emotion: currentVoice?.emotion || '',
          avatarUrl: currentVoice?.avatar,
          sanitizeText,
        }

        const groupId = action.payload.id
        const messages = state.messages.map((message) =>
          message.id === groupId ? { ...message, voices: [...message.voices, newMessage] } : message
        )

        const foundMessagesGroupIndex = messages.findIndex((message) => message.id === groupId)

        if (foundMessagesGroupIndex === -1) {
          const newMessagesGroup = {
            id: groupId,
            isOpened: true,
            text: action.payload.text.original,
            pitch: action.payload.pitch,
            rate: action.payload.rate,
            volume: action.payload.volume,
            voices: [newMessage],
          }

          messages.unshift(newMessagesGroup)
        }

        try {
          setLocalStorageInfo('tts', { ...getLocalStorageInfo('tts'), messages })
          state.messages = messages
          state.loading = false
        } catch (e) {
          state.loading = false
        }
      })
      .addCase(ttsSend.rejected, (state) => {
        state.loading = false
      })
      .addCase(getVoices.fulfilled, (state, action: PayloadAction<IVoiceItem[]>) => {
        return applyVoicesUpdate(state, action.payload)
      })
      .addCase(getTtsConfig.fulfilled, (state, action: PayloadAction<IConfigVoice[]>) => {
        const languages = Array.from(
          new Set(action.payload.map((v) => v.language).filter(Boolean))
        )
        return { ...state, voiceLanguages: languages, ttsConfig: action.payload }
      })
      .addCase(updateVoice.fulfilled, (state, action: PayloadAction<{ response_code: number; voice: IVoiceItem }>) => {
        if (action.payload?.response_code === 0 && action.payload.voice) {
          const updatedVoices = mapVoiceItems([action.payload.voice], state.voices || [])
          const voices = state.voices.map((v) => updatedVoices.find((u) => u.name === v.name) ?? v)
          setLocalStorageInfo('tts', { ...getLocalStorageInfo('tts'), voices })
          return { ...state, voices }
        }
      })
      .addCase(uploadVoiceAvatar.fulfilled, (state, action: PayloadAction<{ response_code: number; voices: IVoiceItem[] }>) => {
        if (action.payload?.response_code === 0 && action.payload.voices) {
          return applyVoicesUpdate(state, action.payload.voices, true)
        }
      })
      .addCase(uploadVoiceSample.fulfilled, (state, action: PayloadAction<{ response_code: number; voices: IVoiceItem[] }>) => {
        if (action.payload?.response_code === 0 && action.payload.voices) {
          return applyVoicesUpdate(state, action.payload.voices, true)
        }
      })
      .addCase(logout, (state) => {
        setLocalStorageInfo('tts', { ...getLocalStorageInfo('tts'), voices: [] })
        return { ...state, voices: [] }
      })
  },
})

const { actions, reducer } = ttsSlice

export const {
  ttsClearMessages,
  ttsSetRemovePickedVoice,
  ttsUpdateSettings,
  ttsUpdateModalFilters,
  ttsUpdateListFilters,
  ttsRemoveMessage,
  ttsShowHideMessage,
  ttsSetAudio,
  ttsUpdatePickedVoices,
  ttsSetEmotionVoice,
  ttsSetUpdateCache,
  ttsToggleShowEmotions,
} = actions

export default reducer
